// Left-anchored slide-in drawer. Built on the same base-ui Dialog
// primitives as BottomSheet so focus trapping, Esc-to-close, and
// overlay behaviour match the rest of the app. Auto-closes on route
// change so navigating via a link inside the drawer dismisses it.

import * as React from "react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { cn } from "@/lib/utils";

interface NavDrawerProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function NavDrawer({
  open,
  onOpenChange,
  title,
  children,
  className,
}: NavDrawerProps) {
  const location = useLocation();
  useEffect(() => {
    if (open) onOpenChange(false);
    // Only react to pathname changes, not to handler identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop
          className={cn(
            "fixed inset-0 z-50 bg-black/10 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
          )}
        />
        <DialogPrimitive.Popup
          className={cn(
            "fixed inset-y-0 left-0 z-50 w-[80vw] max-w-xs border-r border-border bg-bg-sidebar shadow-2xl outline-none",
            "data-open:animate-in data-open:slide-in-from-left data-closed:animate-out data-closed:slide-out-to-left",
            className,
          )}
        >
          {title !== undefined && (
            <DialogPrimitive.Title className="sr-only">
              {title}
            </DialogPrimitive.Title>
          )}
          <div className="flex h-full flex-col overflow-y-auto">{children}</div>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
